import { io, Socket } from 'socket.io-client';
import { BehaviorSubject } from 'rxjs';

export interface SocketConfig {
  url: string;
  namespace?: string;
  options?: {
    transports?: string[];
    timeout?: number;
    /** Automatic reconnection attempts after a dropped transport (default 5). */
    reconnectionAttempts?: number;
    reconnectionDelay?: number;
    reconnectionDelayMax?: number;
  };
  /**
   * Handshake `auth` builder. Called on every (re)connection, so it always
   * sends the current credentials.
   */
  auth?: (cb: (data: object) => void) => void;
}

export interface ConnectionStatus {
  connected: boolean;
  connecting: boolean;
  error?: string;
}

/**
 * Owns exactly one socket.io client socket, created lazily and reused for the
 * lifetime of the service. `connect()` never creates a second socket: it
 * (re)opens the same one, so listeners are registered once and a reconnect
 * can never leave a stale socket behind.
 */
export class SocketService {
  private socket: Socket | null = null;
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>({
    connected: false,
    connecting: false,
  });

  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  constructor(private config: SocketConfig) {}

  /**
   * The socket instance, created (but not connected) on first call. Register
   * event listeners on it once; they survive every reconnect.
   */
  getOrCreateSocket(): Socket {
    if (this.socket) return this.socket;

    const url = this.config.namespace
      ? `${this.config.url}/${this.config.namespace}`
      : this.config.url;
    const options = this.config.options ?? {};

    const socket = io(url, {
      autoConnect: false,
      transports: options.transports ?? ['websocket', 'polling'],
      timeout: options.timeout ?? 20000,
      reconnectionAttempts: options.reconnectionAttempts ?? 5,
      reconnectionDelay: options.reconnectionDelay ?? 1000,
      reconnectionDelayMax: options.reconnectionDelayMax ?? 10000,
      ...(this.config.auth ? { auth: this.config.auth } : {}),
    });

    socket.on('connect', () => {
      this.updateConnectionStatus({ connected: true, connecting: false });
    });
    socket.on('connect_error', (error) => {
      // `active` stays true while socket.io will retry on its own; it is false
      // after a handshake rejection, which socket.io never retries.
      this.updateConnectionStatus({
        connected: false,
        connecting: socket.active,
        error: error.message,
      });
    });
    socket.on('disconnect', () => {
      this.updateConnectionStatus({
        connected: false,
        connecting: socket.active,
      });
    });
    socket.io.on('reconnect_failed', () => {
      this.updateConnectionStatus({
        connected: false,
        connecting: false,
        error: 'Could not reach the server',
      });
    });

    this.socket = socket;
    return socket;
  }

  /**
   * Connect (or wait for the in-flight connection). Resolves on `connect`;
   * rejects when the server refuses the handshake or automatic retries give up.
   */
  connect(): Promise<void> {
    const socket = this.getOrCreateSocket();
    if (socket.connected) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        socket.off('connect', onConnect);
        socket.off('connect_error', onError);
        socket.io.off('reconnect_failed', onGiveUp);
      };
      const onConnect = () => {
        cleanup();
        resolve();
      };
      const onError = (error: Error) => {
        if (socket.active) return; // socket.io will retry by itself
        cleanup();
        reject(error);
      };
      const onGiveUp = () => {
        cleanup();
        reject(new Error('Could not reach the server'));
      };
      socket.on('connect', onConnect);
      socket.on('connect_error', onError);
      socket.io.on('reconnect_failed', onGiveUp);

      if (!socket.active) {
        this.updateConnectionStatus({ connected: false, connecting: true });
        socket.connect();
      }
    });
  }

  /** Close the connection. The socket instance is kept for a later connect(). */
  disconnect(): void {
    if (this.socket?.active) {
      this.socket.disconnect();
    }
    this.updateConnectionStatus({ connected: false, connecting: false });
  }

  /**
   * Check if connected to the server
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get the current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Update connection status and notify subscribers
   */
  private updateConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatusSubject.next(status);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.socket?.disconnect();
    this.socket?.removeAllListeners();
    this.socket = null;
    this.connectionStatusSubject.complete();
  }
}
